import {
  action,
  comparer,
  computed,
  makeObservable,
  observable,
  reaction,
} from 'mobx'

import { ITextMarkupProvider } from './markupEditorTextChannel'
import MarkupTextStore, { ITextBox } from './MarkupTextStore'
import { inputOfBox, ITextMarkupInput, specOfBox } from './markupTextStyle'
import {
  areTextMarkupFacesSettled,
  loadTextMarkupFaces,
} from './textMarkupRegistry'

/**
 * Which of the editor's text boxes are DOM and which GL. Only the selected or editing box is a
 * DOM box, plus one deselected while its font face is still loading: GL would have to leave it
 * out until then, so it stays DOM (lingers) and swaps once the face settles. Every other box is
 * drawn by the session mount's GL layer from textInputs. A swap is a data change: the box leaves
 * one set and joins the other in the same action, so both land in the same frame.
 */
export class MarkupEditorTextSwap implements ITextMarkupProvider {
  private readonly lingering = observable.set<string>()
  private readonly disposers: (() => void)[]

  public constructor(
    private readonly textStore: MarkupTextStore,
    // Gives a box that joins the DOM set its screen position in the same batch.
    project: () => void,
  ) {
    makeObservable(this)
    this.disposers = [
      reaction(() => this.domTextIds, project, { equals: comparer.structural }),
    ]
  }

  @computed({ equals: comparer.structural })
  public get domTextIds(): string[] {
    const { selectedId, editingId, boxes } = this.textStore
    return boxes
      .filter(
        box =>
          box.id === selectedId ||
          box.id === editingId ||
          (this.lingering.has(box.id) &&
            !areTextMarkupFacesSettled(specOfBox(box))),
      )
      .map(box => box.id)
  }

  // A DOM box's fields are never read here, so typing into it re-derives nothing.
  @computed({ equals: comparer.structural })
  public get textInputs(): ITextMarkupInput[] {
    const dom = new Set(this.domTextIds)
    return this.textStore.boxes
      .filter(box => !dom.has(box.id) && box.text !== '')
      .map(inputOfBox)
  }

  @action.bound
  public onSelectionChange(prev: string | null, next: string | null): void {
    if (next != null) this.lingering.delete(next)
    const box = prev == null ? undefined : this.find(prev)
    if (!box || box.text === '') return
    const spec = specOfBox(box)
    if (areTextMarkupFacesSettled(spec)) return
    this.lingering.add(box.id)
    loadTextMarkupFaces([spec])
  }

  public dispose(): void {
    this.disposers.forEach(dispose => dispose())
  }

  private find(id: string): ITextBox | undefined {
    return this.textStore.boxes.find(box => box.id === id)
  }
}
